import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CategoryFilter from "../components/CategoryFilter";
import ProductCard from "../components/ProductCard";
import { api } from "../lib/api";
import { Search } from "lucide-react";

const DEFAULT_CATEGORIES = [
  "All",
  "For Men",
  "For Women",
  "Unisex",
  "For Parties",
  "For Office",
  "For Summer",
  "For Winter",
  "For All Weather",
];

const Shop = () => {
  const [params, setParams] = useSearchParams();
  // URL is the single source of truth so header/footer links work while already on /shop
  const active = params.get("category") || "All";
  const bestOnly = params.get("best") === "1";

  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const updateFilters = (nextCategory, nextBest) => {
    const next = {};
    if (nextCategory && nextCategory !== "All") next.category = nextCategory;
    if (nextBest) next.best = "1";
    setParams(next, { replace: true });
  };

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const query = {};
    if (active && active !== "All") query.category = active;
    if (bestOnly) query.best_seller = true;
    if (search.trim()) query.search = search.trim();
    api
      .get("/products", { params: query })
      .then((r) => {
        let list = r.data || [];
        // Defensive client-side filtering so only matching products ever render
        if (bestOnly) list = list.filter((p) => p.is_best_seller === true);
        if (active && active !== "All") list = list.filter((p) => (p.categories || []).includes(active));
        setProducts(list);
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [active, bestOnly, search]);

  const headline = useMemo(() => {
    if (bestOnly) return { eyebrow: "The most poured", title: "Best", italic: "sellers" };
    if (active === "All") return { eyebrow: "The catalog", title: "Ambdrop", italic: "fragrance" };
    return { eyebrow: "Filtered by", title: active, italic: "" };
  }, [bestOnly, active]);

  return (
    <div data-testid="shop-page" className="min-h-screen">
      <Header />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-12">
        <p className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-6 font-bold">
          {headline.eyebrow}
        </p>
        <div className="flex items-end justify-between gap-8 flex-col md:flex-row">
          <h1
            data-testid="shop-title"
            className="font-serif-display font-light text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight gold-shimmer"
          >
            {headline.title}{" "}
            {headline.italic && <span className="italic">{headline.italic}</span>}
          </h1>

          <div className="w-full md:max-w-xs flex items-center border-b border-[#D4AF37]">
            <Search size={16} strokeWidth={1.2} className="text-[#D4AF37]" />
            <input
              data-testid="shop-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or notes"
              className="flex-1 bg-transparent px-3 py-3 text-sm placeholder:text-[#D4AF37] outline-none"
            />
          </div>
        </div>
      </section>

      <CategoryFilter
        categories={categories}
        active={active}
        onChange={(c) => updateFilters(c, c === "All" ? bestOnly : false)}
      />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="flex items-center justify-between mb-10">
          <p
            data-testid="result-count"
            className="text-[11px] tracking-overline uppercase text-[#B0A496] font-bold"
          >
            {loading ? "Loading…" : `${products.length} fragrance${products.length === 1 ? "" : "s"}`}
          </p>
          <button
            data-testid="toggle-best-only"
            onClick={() => updateFilters(active, !bestOnly)}
            className={`text-[11px] tracking-overline uppercase font-bold transition-colors ${
              bestOnly ? "text-[#F4EAD9] active-cat" : "text-[#D4AF37] hover:text-[#F4EAD9]"
            }`}
          >
            Best sellers only
          </button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="aspect-[4/5] bg-[#1F1A17]" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div data-testid="empty-state" className="py-32 text-center">
            <p className="font-serif-display text-3xl text-[#F4EAD9]">Nothing here yet.</p>
            <p className="mt-3 text-sm text-[#B0A496]">
              Try a different category or clear your search.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {products.map((p, idx) => (
              <ProductCard key={p.id} product={p} index={idx} />
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default Shop;
